import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Add, Delete, Save, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getCategories } from '../store/slices/categorySlice';
import productService from '../services/productService';
import trendingProductService from '../services/trendingProductService';
import { compressImage } from '../utils/imageCompressor';

const TrendingProductForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const categoriesState = useSelector((state) => state.categories);
  const { categories = [], isLoading: categoriesLoading, error: categoriesError } = categoriesState || {};

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [allProducts, setAllProducts] = useState([]);

  const [formData, setFormData] = useState({
    productRef: '',
    name: '',
    description: '',
    shortDescription: '',
    category: '',
    brand: '',
    basePrice: '',
    originalPrice: '',
    discountPercentage: 0,
    sku: '',
    stock: '',
    lowStockThreshold: 5,
    isActive: true,
    isFeatured: false,
    tags: [],
    variants: [],
    images: [],
    specifications: {
      weight: '',
      dimensions: '',
      material: '',
      warranty: '',
      origin: '',
      ingredients: '',
      directions: '',
      precautions: '',
    },
    link: '',
  });

  const [newTag, setNewTag] = useState('');
  const [variantGroups, setVariantGroups] = useState([]);
  const [newVariantGroup, setNewVariantGroup] = useState({ name: '', options: [] });
  const [newVariantOption, setNewVariantOption] = useState({
    value: '',
    price: '',
    originalPrice: '',
    discountPercentage: 0,
    stock: '',
    sku: '',
    image: '',
    isActive: true,
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    dispatch(getCategories());
    // Fetch existing real products so trending item can navigate to product detail page
    productService
      .getProducts({ limit: 1000 })
      .then((res) => setAllProducts(res?.products || []))
      .catch(() => setAllProducts([]));
  }, [dispatch]);

  useEffect(() => {
    const load = async () => {
      if (!isEdit) return;
      setIsLoading(true);
      setError(null);
      try {
        const res = await trendingProductService.get(id);
        const p = res?.product;
        setFormData({
          productRef: p?.productRef || '',
          name: p?.name || '',
          description: p?.description || '',
          shortDescription: p?.shortDescription || '',
          category: p?.category?._id || p?.category || '',
          brand: p?.brand || '',
          basePrice: p?.price ?? '',
          originalPrice: p?.originalPrice ?? '',
          discountPercentage: p?.discountPercentage ?? 0,
          sku: p?.sku || '',
          stock: p?.stock ?? '',
          lowStockThreshold: p?.lowStockThreshold ?? 5,
          isActive: p?.isActive !== false,
          isFeatured: p?.featured === true,
          tags: Array.isArray(p?.tags) ? p.tags : [],
          variants: Array.isArray(p?.variants) ? p.variants : [],
          images: Array.isArray(p?.images) ? p.images : [],
          specifications: p?.specifications || {
            weight: '',
            dimensions: '',
            material: '',
            warranty: '',
            origin: '',
            ingredients: '',
            directions: '',
            precautions: '',
          },
          link: p?.link || '',
        });
        setVariantGroups(Array.isArray(p?.variants) ? p.variants : []);
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || 'Failed to load trending product');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Product name is required';
    if (!formData.brand.trim()) errs.brand = 'Brand is required';
    if (!formData.category) errs.category = 'Category is required';
    if (!formData.basePrice || Number(formData.basePrice) <= 0) errs.basePrice = 'Valid base price is required';
    if (formData.stock === '' || Number(formData.stock) < 0) errs.stock = 'Valid stock quantity is required';
    if (!formData.description.trim()) errs.description = 'Description is required';
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setUploadingImages(true);
    try {
      const uploadPromises = files.map(async (file) => {
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file`);
        }

        let fileToUpload = file;
        try {
          fileToUpload = await compressImage(file, {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
        } catch (_) { }

        const fd = new FormData();
        fd.append('image', fileToUpload);

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com'}/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: fd,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Upload failed');
        }

        const result = await response.json();
        return result.imageUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setFormData((prev) => ({ ...prev, images: [...prev.images, ...uploadedUrls] }));
    } catch (e) {
      console.error('Trending image upload error:', e);
      setError(e?.message || 'Failed to upload image');
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleAddTag = () => {
    const t = newTag.trim();
    if (!t) return;
    if (formData.tags.includes(t)) return;
    setFormData((prev) => ({ ...prev, tags: [...prev.tags, t] }));
    setNewTag('');
  };

  const handleRemoveTag = (tag) => {
    setFormData((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const handleAddVariantGroup = () => {
    if (!newVariantGroup.name.trim()) return;
    setVariantGroups((prev) => [...prev, { name: newVariantGroup.name.trim(), options: [] }]);
    setNewVariantGroup({ name: '', options: [] });
  };

  const handleAddVariantOption = (groupIndex) => {
    if (!newVariantOption.value.trim()) return;
    setVariantGroups((prev) => {
      const updated = [...prev];
      updated[groupIndex] = {
        ...updated[groupIndex],
        options: [
          ...(updated[groupIndex].options || []),
          {
            ...newVariantOption,
            value: newVariantOption.value.trim(),
            price: newVariantOption.price === '' ? undefined : Number(newVariantOption.price),
            originalPrice: newVariantOption.originalPrice === '' ? undefined : Number(newVariantOption.originalPrice),
            discountPercentage: Number(newVariantOption.discountPercentage) || 0,
            stock: newVariantOption.stock === '' ? undefined : Number(newVariantOption.stock),
            sku: newVariantOption.sku || undefined,
            image: newVariantOption.image || undefined,
          },
        ],
      };
      return updated;
    });
    setNewVariantOption({
      value: '',
      price: '',
      originalPrice: '',
      discountPercentage: 0,
      stock: '',
      sku: '',
      image: '',
      isActive: true,
    });
  };

  const handleRemoveVariantGroup = (groupIndex) => {
    setVariantGroups((prev) => prev.filter((_, i) => i !== groupIndex));
  };

  const handleRemoveVariantOption = (groupIndex, optionIndex) => {
    setVariantGroups((prev) => {
      const updated = [...prev];
      updated[groupIndex] = {
        ...updated[groupIndex],
        options: updated[groupIndex].options.filter((_, i) => i !== optionIndex),
      };
      return updated;
    });
  };

  const handleSpecChange = (key) => (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      specifications: { ...(prev.specifications || {}), [key]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage('');
    setValidationErrors({});

    try {
      if (!validateForm()) {
        setIsLoading(false);
        return;
      }

      const { isFeatured, ...rest } = formData;
      const submitData = {
        ...rest,
        basePrice: Number(formData.basePrice),
        price: Number(formData.basePrice),
        originalPrice: formData.originalPrice ? Number(formData.originalPrice) : undefined,
        discountPercentage: formData.discountPercentage ? Number(formData.discountPercentage) : 0,
        sku: formData.sku || undefined,
        stock: Number(formData.stock),
        lowStockThreshold: Number(formData.lowStockThreshold),
        featured: isFeatured,
        variants: variantGroups,
        specifications: Object.fromEntries(Object.entries(formData.specifications || {}).filter(([_, v]) => v !== '')),
      };

      if (isEdit) {
        await trendingProductService.update(id, submitData);
        setSuccessMessage('Trending product updated successfully!');
      } else {
        await trendingProductService.create(submitData);
        setSuccessMessage('Trending product created successfully!');
      }

      setTimeout(() => {
        navigate('/products/trending');
      }, 800);
    } catch (e2) {
      setError(e2?.response?.data?.message || e2?.message || 'Failed to save trending product');
    } finally {
      setIsLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    const placeholder =
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
    if (!imagePath) return placeholder;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com').replace('/api', '');
    const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${apiBaseUrl}${normalizedPath}`;
  };

  if (isLoading || categoriesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/products/trending')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">{isEdit ? 'Edit Trending Product' : 'Add New Trending Product'}</Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {categoriesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Categories Error: {categoriesError}
        </Alert>
      )}

      {Object.keys(validationErrors).length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Validation errors:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {Object.values(validationErrors).map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                required
                error={!!validationErrors.brand}
                helperText={validationErrors.brand}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!validationErrors.category}>
                <InputLabel>Category</InputLabel>
                <Select name="category" value={formData.category} onChange={handleChange} label="Category">
                  {categories && categories.length > 0 ? (
                    categories.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>{categoriesLoading ? 'Loading categories...' : 'No categories available.'}</MenuItem>
                  )}
                </Select>
                {validationErrors.category && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {validationErrors.category}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Base Price"
                name="basePrice"
                value={formData.basePrice}
                onChange={handleChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">PKR</InputAdornment>,
                }}
                required
                error={!!validationErrors.basePrice}
                helperText={validationErrors.basePrice}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Original Price (Before Discount)"
                name="originalPrice"
                value={formData.originalPrice}
                onChange={handleChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">PKR</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Discount Percentage"
                name="discountPercentage"
                value={formData.discountPercentage}
                onChange={handleChange}
                type="number"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth label="SKU (Product Code)" name="sku" value={formData.sku} onChange={handleChange} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Stock Quantity"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                type="number"
                required
                error={!!validationErrors.stock}
                helperText={validationErrors.stock}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Low Stock Threshold"
                name="lowStockThreshold"
                value={formData.lowStockThreshold}
                onChange={handleChange}
                type="number"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={formData.isActive} onChange={handleChange} name="isActive" />}
                label="Active"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={formData.isFeatured} onChange={handleChange} name="isFeatured" />}
                label="Featured"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Short Description"
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
                multiline
                minRows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                multiline
                minRows={4}
                error={!!validationErrors.description}
                helperText={validationErrors.description}
              />
            </Grid>

            {/* Tags */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Tags
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Add Tag" value={newTag} onChange={(e) => setNewTag(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4} display="flex" alignItems="center">
              <Button variant="contained" startIcon={<Add />} onClick={handleAddTag} sx={{ backgroundColor: '#ff2852', color: '#000' }}>
                Add Tag
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={1} flexWrap="wrap">
                {formData.tags.map((tag) => (
                  <Chip key={tag} label={tag} onDelete={() => handleRemoveTag(tag)} />
                ))}
              </Box>
            </Grid>

            {/* Variants */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Variants
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Variant Group Name (e.g., Size, Color)"
                value={newVariantGroup.name}
                onChange={(e) => setNewVariantGroup({ name: e.target.value, options: [] })}
              />
            </Grid>
            <Grid item xs={12} md={4} display="flex" alignItems="center">
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddVariantGroup}
                sx={{ backgroundColor: '#ff2852', color: '#000' }}
              >
                Add Group
              </Button>
            </Grid>

            {variantGroups.map((group, groupIndex) => (
              <Grid item xs={12} key={`${group.name}-${groupIndex}`}>
                <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {group.name}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleRemoveVariantGroup(groupIndex)}
                    >
                      Remove Group
                    </Button>
                  </Box>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Value"
                        value={newVariantOption.value}
                        onChange={(e) => setNewVariantOption((p) => ({ ...p, value: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Price"
                        value={newVariantOption.price}
                        onChange={(e) => setNewVariantOption((p) => ({ ...p, price: e.target.value }))}
                        type="number"
                        InputProps={{ startAdornment: <InputAdornment position="start">PKR</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Stock"
                        value={newVariantOption.stock}
                        onChange={(e) => setNewVariantOption((p) => ({ ...p, stock: e.target.value }))}
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Button
                        variant="contained"
                        onClick={() => handleAddVariantOption(groupIndex)}
                        sx={{ backgroundColor: '#ff2852', color: '#000', fontWeight: 700 }}
                        fullWidth
                      >
                        Add Option
                      </Button>
                    </Grid>
                  </Grid>

                  {(group.options || []).length > 0 && (
                    <Box mt={2}>
                      {(group.options || []).map((opt, optionIndex) => (
                        <Box
                          key={`${opt.value}-${optionIndex}`}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1,
                            borderBottom: '1px solid #f0f0f0',
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={700}>
                              {opt.value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              PKR {opt.price ?? formData.basePrice} • Stock {opt.stock ?? '-'}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Delete />}
                            onClick={() => handleRemoveVariantOption(groupIndex, optionIndex)}
                          >
                            Remove
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}

            {/* Specifications */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Specifications
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            {Object.keys(formData.specifications || {}).map((key) => (
              <Grid item xs={12} md={6} key={key}>
                <TextField
                  fullWidth
                  label={key}
                  value={formData.specifications?.[key] || ''}
                  onChange={handleSpecChange(key)}
                />
              </Grid>
            ))}

            {/* Media */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Images
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" disabled={uploadingImages}>
                {uploadingImages ? 'Uploading...' : 'Upload Images'}
                <input type="file" accept="image/*" hidden multiple onChange={handleImageUpload} />
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {(formData.images || []).map((img, idx) => (
                  <Grid item xs={6} sm={4} md={3} key={img + idx}>
                    <Card>
                      <CardMedia component="img" height="160" image={getImageUrl(img)} alt={`img-${idx}`} />
                      <CardContent>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleRemoveImage(idx)}
                          fullWidth
                        >
                          Remove
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Optional Link */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Navigation
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Navigate to Real Product (optional)</InputLabel>
                <Select
                  name="productRef"
                  value={formData.productRef || ''}
                  onChange={handleChange}
                  label="Navigate to Real Product (optional)"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {allProducts.map((p) => (
                    <MenuItem key={p._id} value={p._id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link (optional fallback)"
                name="link"
                value={formData.link}
                onChange={handleChange}
                placeholder="/products/xyz OR https://..."
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              type="submit"
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={18} /> : <Save />}
              disabled={isLoading || uploadingImages}
              sx={{
                backgroundColor: '#ff2852',
                color: '#000000',
                fontWeight: 700,
                '&:hover': { backgroundColor: '#5A189A' },
              }}
            >
              Save
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default TrendingProductForm;


